import {
	createFileRoute,
	redirect,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { fetchQuery, graphql, useMutation } from "react-relay";
import { toast } from "sonner";
import { useOnboardingAnalytics } from "@/analytics/onboarding";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Card } from "@/components/ui/card";
import {
	type EventFormSchemaValues,
	OnboardingEventForm,
} from "@/forms/onboarding-event-form";
import { environment } from "@/relay/environment";
import type { eventosCreateMutation } from "./__generated__/eventosCreateMutation.graphql";
import type { eventosLoaderQuery } from "./__generated__/eventosLoaderQuery.graphql";
import type { eventosOnboardingUpdateMutation } from "./__generated__/eventosOnboardingUpdateMutation.graphql";

export const Route = createFileRoute("/_logged/onboarding/$club/eventos")({
	component: RouteComponent,
	loader: async ({ params }) => {
		const data = await fetchQuery<eventosLoaderQuery>(
			environment,
			graphql`
        query eventosLoaderQuery($club: String!) {
          club(slug: $club) {
            eventsEnabled: configuration(key: "EVENTS_ENABLED")
          }
        }
      `,
			{
				club: params.club,
			},
		).toPromise();

		if (
			data?.club.eventsEnabled !== "1" &&
			data?.club.eventsEnabled !== "ENABLED"
		) {
			throw redirect({
				to: "/onboarding/$club/ranking",
				params: { club: params.club },
			});
		}

		return {};
	},
});

function RouteComponent() {
	const { session } = useRouteContext({ from: "__root__" });
	const { club } = useRouteContext({ from: "/_logged/onboarding/$club" });
	const params = useParams({ strict: false });
	const clubSlug = params.club as string;
	const navigate = useNavigate();
	const { capture, trackError } = useOnboardingAnalytics("events", clubSlug);

	const [updateClub] = useMutation<eventosOnboardingUpdateMutation>(
		graphql`
      mutation eventosOnboardingUpdateMutation($club: ID!, $payload: UpdateClubInput!) {
        updateClub(id: $club, payload: $payload) {
          id
        }
      }
    `,
	);

	const [createEvent] = useMutation<eventosCreateMutation>(graphql`
   mutation eventosCreateMutation($club: String!, $payload: CreateEventInput!) {
       createEvent(payload: $payload, club: $club) {
           eventEdge {
               node {
                 id
               }
           }
       }
   }
 `);

	const submit = (value: EventFormSchemaValues) => {
		return new Promise((resolve, reject) => {
			createEvent({
				variables: {
					club: clubSlug,
					payload: {
						title: value.title,
						description: JSON.stringify(value.description),
						host: session?.user?.id!,
						visibility: "PUBLIC",
						startDate: value.startDate,
						endDate: value.endDate,
						eventType: "LIVE",
						coverPhoto: value.cover?.id,
						url: value.url,
						badges: [],
						tags: [],
						products: [],
						sendPromotionEmail: true,
						sendConfirmationEmail: true,
						sendReminderEmail: true,
					},
				},
				onCompleted: (response, errors) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						reject(new Error(errors[0].message));
						return;
					}
					updateClub({
						variables: {
							club: club?.id ?? "",
							payload: {
								configurations: [
									{
										key: "ONBOARDING_LAST_PAGE",
										value: "events",
									},
								],
							},
						},
						onCompleted: (_clubResponse, clubErrors) => {
							if (clubErrors?.length) {
								toast.error(clubErrors[0].message);
								reject(new Error(clubErrors[0].message));
								return;
							}
							capture("completed", {
								event_created: true,
								event_title: value.title,
							});
							toast.success("Evento criado com sucesso!");
							navigate({
								to: "/onboarding/$club/ranking",
								params: { club: clubSlug },
							});
							resolve(response);
						},
						onError: (error) => {
							toast.error("Erro ao salvar o progresso do onboarding.");
							reject(error);
						},
					});
				},
				onError: (error) => {
					trackError("submission", { error_message: error.message });
					reject(error);
				},
			});
		});
	};

	return (
		<div className="container mx-auto p-6 max-w-2xl flex flex-col">
			<Card className="p-6 gap-0">
				<div className="mb-6">
					<Heading>Crie seu primeiro evento</Heading>

					<Text>
						Preparei um evento de inauguração como exemplo. Edite como preferir!
					</Text>
				</div>

				<OnboardingEventForm club={club!} onSubmit={submit} />
			</Card>
		</div>
	);
}
